/**
 * 小哨與 AI 頁的「AI 進階功能」：照片描述、聲音轉文字、語意搜尋、趨勢預警，各自開關、設定與測試。
 *
 * API：
 *   GET  /api/v1/assistant/services              — 四項功能的設定、狀態與處理數量
 *   PUT  /api/v1/assistant/services/{kind}       — 儲存單一功能；api_key 留空代表沿用
 *   POST /api/v1/assistant/services/{kind}/test  — 用表單上的值測試，送的是程式產生的測試圖片或靜音
 */
import { useEffect, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Image as ImageIcon, Loader2, Mic, PlugZap, Search, TrendingDown, XCircle } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { serviceMissing, serviceStatsText, serviceStatus, type ServiceForm } from '@/lib/aiServices';
import { fmtTime } from '@/lib/format';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SettingSwitch } from '@/components/ui/setting-switch';
import type { AiService, AiServiceKind, AiServiceProbe, AiServicesResponse } from '@/types/assistant';

const QUERY_KEY = ['assistant', 'services'];

const META: Record<AiServiceKind, { icon: ComponentType<{ className?: string }>; summary: string; hint: string }> = {
  vision: {
    icon: ImageIcon,
    summary: '替新保存的照片寫一段客觀描述，事件詳情會顯示，小哨也能依內容找照片。',
    hint: '需要能看圖的模型（llama.cpp 要加上 --mmproj）。網址留空會沿用上方的對話模型與金鑰。',
  },
  stt: {
    icon: Mic,
    summary: '把新保存的錄音轉成逐字稿，事件詳情會顯示，小哨也能依對話內容找錄音。',
    hint: 'whisper.cpp server 填 http://主機:埠號，會自動補上 /inference；OpenAI 相容服務填到 /v1。',
  },
  embed: {
    icon: Search,
    summary: '把描述與逐字稿轉成向量，用意思找檔案，不必猜對關鍵字。沒開時小哨改用關鍵字比對。',
    hint: 'llama.cpp 要加上 --embeddings 啟動，建議另開一個向量模型（例如 bge-m3）。網址留空沿用對話模型。',
  },
  trend: {
    icon: TrendingDown,
    summary: '定時記錄在線裝置的 SD 卡、記憶體與開機時間，預估快滿、記憶體一直下降或頻繁重開機時提早通知。',
    hint: '',
  },
};

const LANGUAGES = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: '英文' },
  { value: 'ja', label: '日文' },
  { value: 'auto', label: '自動偵測' },
];

const TONE = { success: 'success', warn: 'warn', danger: 'danger', muted: 'muted' } as const;

export function AiServicesCard({ style }: { style?: CSSProperties }) {
  const services = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiGet<AiServicesResponse>('/api/v1/assistant/services'),
  });

  return (
    <Card className="reveal" style={style}>
      <CardHeader>
        <div>
          <CardTitle>AI 進階功能</CardTitle>
          <CardDescription>
            四項功能各自開關，預設都是關閉。開啟後只處理最近 7 天保存的檔案，每輪處理幾個，不會一次全部送出。
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <p className="rounded-md border border-border bg-secondary p-3 text-[12px] leading-relaxed text-muted-foreground">
          <strong className="text-foreground">資料會送到哪裡：</strong>
          照片描述與聲音轉文字會把照片、音檔送到下面填的伺服器；語意搜尋只送描述與逐字稿的文字；趨勢預警只在本機計算，不送任何資料。
        </p>
        {services.isPending && <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {services.isError && <p className="text-[12.5px] text-destructive" role="alert">
          載入失敗：{services.error instanceof Error ? services.error.message : '未知錯誤'}
        </p>}
        {services.data?.services?.map((svc, i) => (
          <ServicePanel key={svc.kind} svc={svc} chat={services.data.chat} index={i} />
        ))}
      </CardContent>
    </Card>
  );
}

function toForm(svc: AiService): ServiceForm {
  return { enabled: svc.enabled, base_url: svc.base_url ?? '', model: svc.model ?? '', language: svc.language ?? 'zh' };
}

function ServicePanel({ svc, chat, index }: { svc: AiService; chat: AiServicesResponse['chat']; index: number }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ServiceForm>(() => toForm(svc));
  const [apiKey, setApiKey] = useState('');
  const [clearKey, setClearKey] = useState(false);
  const [probe, setProbe] = useState<AiServiceProbe | null>(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const meta = META[svc.kind];
  const Icon = meta.icon;
  const id = `ai-svc-${svc.kind}`;
  const hasFields = svc.kind !== 'trend';

  // 儲存後伺服器回傳整理過的網址（例如補上 /inference），表單跟著更新
  useEffect(() => { setForm(toForm(svc)); }, [svc.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = () => ({ ...form, api_key: apiKey.trim() || undefined, clear_api_key: clearKey });
  const save = useMutation({
    mutationFn: () => apiPut<AiServicesResponse>(`/api/v1/assistant/services/${svc.kind}`, body()),
    onMutate: () => { setError(''); setSaved(''); },
    onSuccess: (next) => {
      qc.setQueryData(QUERY_KEY, next);
      void qc.invalidateQueries({ queryKey: ['assistant', 'status'] });
      setApiKey('');
      setClearKey(false);
      setSaved(form.enabled ? '已儲存並開啟。' : '已儲存，目前是關閉的。');
    },
    onError: (e) => setError(e instanceof Error ? e.message : '儲存失敗'),
  });
  const test = useMutation({
    mutationFn: () => apiPost<AiServiceProbe>(`/api/v1/assistant/services/${svc.kind}/test`, body()),
    onMutate: () => setProbe(null),
    onSuccess: (r) => setProbe(r),
    onError: (e) => setProbe({ ok: false, latency_ms: null, reply: null, dims: null, model: null,
      error: e instanceof Error ? e.message : '測試失敗' }),
  });

  const set = <K extends keyof ServiceForm>(key: K, value: ServiceForm[K]) => { setForm({ ...form, [key]: value }); setSaved(''); };
  const missing = serviceMissing(svc.kind, form, chat.configured);
  const status = serviceStatus(svc);
  const stats = serviceStatsText(svc);
  const chatHint = chat.model ? `留空沿用對話模型（${chat.model}）` : '留空沿用對話模型';

  return (
    <section aria-labelledby={`${id}-title`} className="reveal rounded-md border border-border p-4" style={order(index)}>
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md bg-secondary text-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={`${id}-title`} className="text-[14px] font-semibold">{svc.label}</h3>
            <Badge variant={TONE[status.tone]}>{status.label}</Badge>
          </div>
          <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{meta.summary}</p>
        </div>
      </div>

      <div className="mt-3">
        <SettingSwitch label={`開啟${svc.label}`} checked={form.enabled}
          description={form.enabled && missing ? missing : svc.kind === 'trend'
            ? '每 10 分鐘取樣一次，資料保留 14 天；樣本約 6 小時後才夠估算。'
            : '關閉後停止處理新檔案，已產生的文字會保留。'}
          onChange={(on) => set('enabled', on)} />
      </div>

      {hasFields && (
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Row label={svc.kind === 'stt' ? '伺服器網址' : '網址'} htmlFor={`${id}-url`}>
            <Input id={`${id}-url`} value={form.base_url} inputMode="url" autoComplete="off" spellCheck={false}
              placeholder={svc.kind === 'stt' ? 'http://伺服器位址:埠號' : chatHint}
              onChange={(e) => set('base_url', e.target.value)} />
          </Row>
          <Row label={svc.kind === 'stt' ? '模型（選填）' : '模型'} htmlFor={`${id}-model`}>
            <Input id={`${id}-model`} value={form.model} autoComplete="off" spellCheck={false}
              placeholder={svc.kind === 'stt' ? 'whisper.cpp 不用填' : chatHint}
              onChange={(e) => set('model', e.target.value)} />
          </Row>
          {svc.kind === 'stt' && (
            <Row label="語言" htmlFor={`${id}-lang`}>
              <select id={`${id}-lang`} value={form.language} onChange={(e) => set('language', e.target.value)}
                className="min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm">
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Row>
          )}
          <Row label="API 金鑰" htmlFor={`${id}-key`}>
            <Input id={`${id}-key`} type="password" value={apiKey} autoComplete="new-password" spellCheck={false}
              disabled={clearKey}
              placeholder={svc.api_key_set ? `已設定（結尾 ${svc.api_key_hint ?? '****'}），留空沿用` : svc.kind === 'stt' ? '選填' : '選填，留空沿用對話模型的金鑰'}
              onChange={(e) => { setApiKey(e.target.value); setSaved(''); }} />
            {svc.api_key_set && (
              <label className="mt-2 inline-flex min-h-[36px] cursor-pointer items-center gap-2 text-[12px]">
                <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={clearKey}
                  onChange={(e) => { setClearKey(e.target.checked); if (e.target.checked) setApiKey(''); }} />
                儲存時清除這個金鑰
              </label>
            )}
          </Row>
          {meta.hint && <p className="text-[11.5px] leading-relaxed text-muted-foreground sm:col-span-2">{meta.hint}</p>}
        </div>
      )}

      {(stats || svc.last_error) && (
        <div className="mt-3 space-y-1 text-[12px] leading-relaxed">
          {stats && <p className="text-muted-foreground">{stats}</p>}
          {svc.last_error && <p className="text-destructive" role="alert">
            {svc.last_error_at ? `${fmtTime(svc.last_error_at)}：` : ''}{svc.last_error}
          </p>}
        </div>
      )}

      {probe && (
        <div className="callout mt-3 flex items-start gap-2 border-t border-border pt-3 text-[12.5px]" role="status">
          {probe.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
          <span className="min-w-0 leading-relaxed">
            {probe.ok ? <>
              連線正常{probe.latency_ms !== null ? `，花了 ${probe.latency_ms} 毫秒` : ''}。
              {probe.reply && <span className="block text-muted-foreground">回覆：{probe.reply}</span>}
              {probe.dims && <span className="block text-muted-foreground">向量長度 {probe.dims}</span>}
            </> : <span className="text-destructive">{probe.error}</span>}
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" disabled={save.isPending || (form.enabled && !!missing)} onClick={() => save.mutate()}>
          {save.isPending ? '儲存中…' : '儲存'}
        </Button>
        {hasFields && (
          <Button type="button" size="sm" variant="secondary" disabled={test.isPending || !!serviceMissing(svc.kind, { ...form, enabled: true }, chat.configured)}
            onClick={() => test.mutate()}>
            {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            {test.isPending ? '測試中…' : '測試連線'}
          </Button>
        )}
        <p className="min-w-0 flex-1 text-[12px]" role="status" aria-live="polite">
          {error ? <span className="text-destructive">{error}</span>
            : saved ? <span className="text-success-on">{saved}</span> : null}
        </p>
      </div>
    </section>
  );
}

function Row({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="mb-2 block text-[12.5px] font-semibold">{label}</label>
      {children}
    </div>
  );
}
